import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Calendar } from "lucide-react";
import { Api } from "../lib/api";
import HeroEffects from "./home/HeroEffects";
import Countdown from "./home/Countdown";
import DaysFlow from "./home/DaysFlow";

export default function HomePage() {
  const [next, setNext] = useState(null);
  const [concepts, setConcepts] = useState([]);

  useEffect(() => {
    Api.events().then((evs) => setNext(evs[0] || null)).catch(() => {});
    Api.concepts().then(setConcepts).catch(() => {});
  }, []);

  return (
    <div className="animate-fadeUp" data-testid="home-page">
      {/* HERO — poster image, untouched */}
      <section
        className="relative overflow-hidden bg-navy"
        data-testid="hero-section"
      >
        <div className="relative w-full max-h-[78vh] aspect-square md:aspect-[16/10] overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_info-unpacker-1/artifacts/6ipiraqz_NOW-TRIP-1.jpg"
            alt="Charlie King presents Picnic in the Park · The Genesis"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 22%" }}
          />
          {/* Drifting cloud puffs + confetti + butterflies */}
          <div className="cloud-puff cloud-puff--a" aria-hidden />
          <div className="cloud-puff cloud-puff--b" aria-hidden />
          <div className="cloud-puff cloud-puff--c" aria-hidden />
          <HeroEffects />
          <div
            className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-round bg-cream/95 backdrop-blur px-4 py-2 text-navy text-[10px] font-bold tracking-[0.18em] uppercase shadow-soft"
            aria-label="Event dates"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-coral" />
            The Genesis · August 19–20 · 2026 · Grinda
          </div>
        </div>
      </section>

      {/* HERO CTA strip — sits beneath the poster */}
      <section className="bg-navy text-cream">
        <div className="container-x py-8 md:py-10 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <p className="max-w-xl text-cream/85 text-base md:text-lg leading-relaxed">
            <span className="italic-accent text-coral font-serif text-xl md:text-2xl block">
              The Genesis.
            </span>
            Two days of discovery and thanksgiving on Grinda — the bow at the end
            of a great summer in the Stockholm archipelago.
          </p>
          <div className="flex gap-3 flex-wrap md:ml-auto">
            <Link to="/book" className="btn-primary" data-testid="hero-book-btn">
              Book your day <ArrowRight size={18} />
            </Link>
            <Link
              to="/programme"
              className="inline-flex items-center gap-2 rounded-round bg-cream text-navy font-bold px-6 py-3 hover:bg-sand transition-colors shadow-soft"
              data-testid="hero-programme-btn"
            >
              See programme
            </Link>
          </div>
        </div>
      </section>

      {/* COUNTDOWN — disappears once the festival opens */}
      <Countdown />

      {/* UP NEXT */}
      {next && (
        <section className="container-x -mt-12 md:-mt-16 relative z-10">
          <div
            className="card p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8"
            data-testid="up-next-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-coral/15 text-coral flex items-center justify-center">
                <Calendar size={20} />
              </div>
              <div>
                <p className="eyebrow">Up next</p>
                <p className="font-serif text-2xl text-navy">{next.concept_name}</p>
                <p className="text-grey text-sm mt-1">
                  <MapPin size={12} className="inline mr-1" />
                  {next.location} · {next.weekday} {next.month_short} {next.day} ·{" "}
                  {next.time_range}
                </p>
              </div>
            </div>
            <Link to="/book" className="btn-primary md:ml-auto">
              Book <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      {/* PROGRAMME PREVIEW */}
      <section className="container-x py-20 md:py-28">
        <p className="eyebrow">August 19–20 · 2026 · Grinda</p>
        <h2 className="heading-xl mt-2">Two signature days.</h2>
        <h2 className="font-serif italic text-coral text-4xl md:text-6xl leading-tight">
          Discovery &amp; thanksgiving.
        </h2>
        <p className="body-lead mt-4 max-w-2xl">
          The Genesis is the bow at the end of the summer. Day One opens with
          discovery; Day Two closes with gratitude.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {concepts.map((c) => (
            <Link
              key={c.concept_id}
              to="/programme"
              className="block rounded-2xl p-7 text-cream transition-transform hover:-translate-y-1 shadow-soft"
              style={{ backgroundColor: c.color }}
              data-testid={`concept-preview-${c.concept_id}`}
            >
              <p
                className="text-[10px] font-bold tracking-[0.2em] uppercase text-cream/80"
              >
                {c.concept_id === "genesis-one"
                  ? "Day one · 19 Aug"
                  : c.concept_id === "genesis-two"
                  ? "Day two · 20 Aug"
                  : "A signature day"}
              </p>
              <p className="font-serif text-3xl mt-2">{c.concept_name}</p>
              <p className="mt-4 text-sm text-cream/85 max-w-md">{c.blurb}</p>
              <div className="mt-6 inline-flex items-center text-xs font-bold tracking-wide text-cream">
                See what to expect <ArrowRight size={12} className="ml-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DAYS FLOW — Discovery → Thanksgiving timeline */}
      <DaysFlow />

      {/* CONTACT CTA */}
      <section className="bg-sand py-16 md:py-24 mt-12">
        <div className="container-x flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="eyebrow">Get in touch</p>
            <h2 className="font-serif text-3xl md:text-5xl mt-1 text-navy">
              Questions, ideas or <span className="italic-accent">a collaboration?</span>
            </h2>
            <p className="text-navy/80 max-w-lg mt-2">
              We&apos;d love to hear from you. Drop us a line and we&apos;ll come back within 48 hours.
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2">
            <Link to="/contact" className="btn-primary">
              Send us a message <ArrowRight size={18} />
            </Link>
            <a
              href="mailto:hello@picnicinthepark.se"
              className="inline-flex items-center gap-2 text-navy font-bold text-sm"
            >
              <Mail size={14} />
              hello@picnicinthepark.se
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy text-cream py-12">
        <div className="container-x">
          <p className="font-serif italic text-2xl text-coral">King × Grinda</p>
          <p className="text-sand/80 text-sm mt-3 max-w-xl leading-relaxed">
            Picnic in the Park is a signature collaboration between King Productions AB and Grinda
            Wärdshus. © 2026.
          </p>
        </div>
      </footer>
    </div>
  );
}
